import { Navigate, useLocation } from 'react-router-dom';
import { hasRoleAccess } from '@/lib/role-access';
import { useAuthStore } from '@/stores/auth-store';
import type { UserRole } from '@/types';

interface Props {
  children: React.ReactNode;
  /** Optional: restrict to specific roles */
  roles?: string[];
}

/**
 * Route guard component.
 * Redirects to /login if not authenticated.
 * Optionally restricts access by role.
 */
export function ProtectedRoute({ children, roles }: Props) {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Role check
  if (roles && roles.length > 0 && user) {
    if (!hasRoleAccess(user.role, roles as UserRole[])) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-900">Access Denied</h2>
            <p className="text-sm text-gray-500 mt-1">
              You don't have permission to access this page.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Required role: {roles.join(' or ')} · Your role: {user.role}
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
